/* By Morning search — the demo's continuous pinch/pull Genie, reflected upward.
 * Original search components own queries and keyboard handling. This popover
 * leaves page scrolling enabled; other drawers retain their own scroll locks.
 * This adapter owns only animation and tells the theme when motion has settled.
 */
(() => {
  'use strict';
  if (window.ByMorningGenie) { window.ByMorningGenie.init(document); return; }
  const clamp = (x, min = 0, max = 1) => Math.min(max, Math.max(min, x));
  const mix = (a, b, t) => a + (b - a) * t;
  const smooth = x => { x = clamp(x); return x*x*x*(x*(x*6-15)+10); };

  // q=0 is the panel; q=1 is the icon. First pinch its near edge, then pull
  // the far edge through the same smooth funnel. Coordinates run upward.
  function edge(u, q, from, to) {
    // Collapse the aperture itself in the final part of the motion. Keeping
    // a fixed target while the spring settles leaves a visible block.
    const aperture = 1 - smooth((q - .74) / .20);
    to = {
      x: to.x + to.width * (1-aperture)/2,
      y: to.y + to.height * (1-aperture)/2,
      width: to.width * aperture,
      height: to.height * aperture,
    };
    const pinch = smooth(q / .54), pull = smooth((q - .20) / .80);
    const x = mix(mix(from.x, to.x, pull), mix(from.x + from.width, to.x + to.width, pinch), u);
    const neckStart = from.x + from.width * .06;
    // Keep curving all the way into the icon. Reaching the minimum width
    // early creates a straight rectangular tube instead of a tapered funnel.
    const neckEnd = mix(from.x + from.width, to.x + to.width, pinch);
    const bend = pinch * smooth((x - neckStart) / Math.max(1, neckEnd - neckStart));
    const center = mix(from.y + from.height / 2, to.y + to.height / 2, bend);
    const height = mix(from.height, to.height, bend);
    return { x, top: center - height / 2, bottom: center + height / 2 };
  }

  // Exact projective mapping of a horizontal source strip into a trapezoid.
  function matrix(length, width, a, b) {
    const h0 = Math.max(.001, a.bottom-a.top), h1 = Math.max(.001, b.bottom-b.top);
    const ratio = h0/h1;
    return `matrix3d(${h0/width},0,0,0,${(ratio*b.top-a.top)/length},${(ratio*b.x-a.x)/length},0,${(ratio-1)/length},0,0,1,0,${a.top},${a.x},0,1)`;
  }

  class SearchGenie {
    constructor(host, header) {
      this.host = host; this.header = header;
      this.details = host.querySelector('details');
      this.toggle = host.querySelector('[data-popdown-toggle]');
      this.body = host.querySelector('[data-popdown-body]');
      this.main = host.querySelector('.search-popdown__main');
      this.form = this.main.querySelector('form.search-form');
      this.row = this.main.querySelector('[data-search-form-inner]');
      this.q = this.details.open ? 0 : 1; this.target = this.q; this.velocity = 0;
      this.frame = 0; this.strips = []; this.layers = []; this.surfaces = [];
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.events = new AbortController();
      this.original = { open: host.open, close: host.close, onClose: host.onClose, transition: host.popdownTransitionCallback };
      host.classList.add('sb-genie');
      host.open = () => this.open();
      host.close = event => this.close(event);
      host.onClose = () => { host.a11y.removeTrapFocus(); this.clear(); this.q = this.target = 1; this.velocity = 0; this.callWithoutScrollEvent(this.original.onClose, 'theme:scroll:unlock'); };
      // Descendant CSS transitions must never finish the native close early.
      host.popdownTransitionCallback = () => {};
      this.toggle.addEventListener('click', event => {
        if (this.details.open && this.target === 1) {
          event.preventDefault(); event.stopImmediatePropagation(); this.open();
        }
      }, { capture: true, signal: this.events.signal });
      this.details.addEventListener('toggle', () => {
        if (!this.details.open) { this.clear(); this.details.classList.remove('is-open'); }
      }, { signal: this.events.signal });
      document.addEventListener('theme:resize', () => this.resize(), { signal: this.events.signal });
      document.addEventListener('theme:scroll', () => {
        if (this.details.open && this.host.offsetParent) this.place();
      }, { signal: this.events.signal });
      window.addEventListener('resize', () => this.resize(), { signal: this.events.signal });
      this.motion.addEventListener('change', () => { if (this.frame) this.finish(); }, { signal: this.events.signal });
    }
    get duration() { return clamp(Number(this.header.dataset.sbDuration) || 520, 200, 1600); }
    callWithoutScrollEvent(method, eventName) {
      // Suppress only the stock search method's synchronous, untargeted event.
      // Never globally unlock the page or interfere with cart/menu scroll locks.
      const skip = event => {
        if (event.target === document && event.detail == null) event.stopImmediatePropagation();
      };
      document.addEventListener(eventName, skip, true);
      try { return method.call(this.host); }
      finally { document.removeEventListener(eventName, skip, true); }
    }
    place() {
      const group = this.toggle.closest('[data-nav-icons], [data-nav-items-compress], [data-mobile-menu]') || this.toggle;
      this.body.style.setProperty('--sb-right', '0px');
      this.body.style.setProperty('--sb-right', Math.max(8, Math.round(this.body.getBoundingClientRect().right - group.getBoundingClientRect().right + 4)) + 'px');
    }
    restoreResults() {
      const search = this.host.searchFormWrapper;
      if (this.host.predictiveSearch && search.input.value.trim() && search.getAttribute('results') === 'true') search.open();
      search.input.setAttribute('aria-expanded', 'true');
    }
    open() {
      const reversing = this.details.open && this.details.classList.contains('sb-closing');
      this.details.classList.remove('sb-closing');
      this.restoreResults();
      this.place();
      if (reversing) this.details.classList.add('is-open');
      else this.callWithoutScrollEvent(this.original.open, 'theme:scroll:lock');
      this.run(0);
    }
    close(event) {
      if (!this.details.open || this.target === 1) return;
      // Match the stock selected-result guard before creating a snapshot.
      const selected = this.host.searchFormWrapper.input.getAttribute('aria-activedescendant');
      if (event && event.target === this.host.popdownClose && selected) return;
      // Capture results BEFORE the native close hides them.
      if (!this.motion.matches && !this.layers.length) this.prepare();
      this.original.close.call(this.host, event);
      this.details.classList.add('sb-closing');
      this.run(1);
    }
    prepare() {
      if (this.layers.length) return true;
      const br = this.body.getBoundingClientRect(), mr = this.main.getBoundingClientRect(), tr = this.toggle.getBoundingClientRect();
      if (mr.width < 1 || mr.height < 1) return false;
      const width = mr.width, height = mr.height;
      this.from = { x: -(mr.bottom-br.top), y: mr.left-br.left, width: height, height: width };
      const mouth = 16, depth = 2;
      this.to = { x: -(tr.top+tr.height/2-br.top)-depth/2, y: tr.left+tr.width/2-br.left-mouth/2, width: depth, height: mouth };
      this.radius = Math.min(parseFloat(getComputedStyle(this.main).borderRadius) || 28, width/2, height/2);
      const left = Math.min(this.from.y, this.to.y)-24;
      const top = Math.min(mr.top-br.top, -this.to.x-this.to.width)-24;
      const right = Math.max(this.from.y+width, this.to.y+this.to.height)+24;
      const bottom = mr.bottom-br.top+24;
      this.bounds = { left, top, width: right-left, height: bottom-top };
      const layer = document.createElement('div');
      layer.className = 'sb-genie-layer'; layer.setAttribute('aria-hidden', 'true'); layer.inert = true;
      const clip = document.createElement('div'); clip.className = 'sb-gclip';
      Object.assign(clip.style, { left: `${left}px`, top: `${top}px`, width: `${right-left}px`, height: `${bottom-top}px` });
      // Blur and clipping must be on this SAME element: a clipping ancestor
      // becomes a backdrop root and prevents its child from sampling the page.
      layer.append(clip); this.clip = clip;
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.classList.add('sb-genie-edge');
      Object.assign(svg.style, { left: `${left}px`, top: `${top}px`, width: `${right-left}px`, height: `${bottom-top}px` });
      this.outline = document.createElementNS(svg.namespaceURI, 'path');
      this.outline.setAttribute('fill', 'none'); this.outline.setAttribute('stroke', 'rgba(255,255,255,.45)');
      this.outline.setAttribute('stroke-width', '1'); svg.append(this.outline);

      const surfaces = document.createElementNS(svg.namespaceURI, 'svg');
      surfaces.classList.add('sb-genie-edge');
      surfaces.style.cssText = svg.style.cssText;
      layer.append(surfaces);
      const results = this.host.searchFormWrapper.getAttribute('open') === 'true' && this.host.searchFormWrapper.getAttribute('results') === 'true';
      const activeResults = this.form.querySelector(results ? '.predictive-search' : '.popular-searches');
      const controls = '.search-form__input, .search-popdown__close, .predictive-search__link';
      // Paint translucent controls ONCE. Overlapping DOM strips must contain
      // only their foreground; otherwise each overlap doubles the fill alpha
      // and produces the bright horizontal lines seen on the input and chips.
      this.form.querySelectorAll(controls).forEach(control => {
        const resultPanel = control.closest('.search-popdown__results > *');
        if (resultPanel && resultPanel !== activeResults) return;
        const rect = control.getBoundingClientRect(), style = getComputedStyle(control);
        if (!rect.width || !rect.height) return;
        const viewport = resultPanel?.getBoundingClientRect();
        if (viewport && (rect.bottom <= viewport.top || rect.top >= viewport.bottom)) return;
        const x = rect.left-mr.left, y = rect.top-mr.top;
        const radius = Math.min(parseFloat(style.borderTopLeftRadius) || 0, rect.width/2, rect.height/2);
        const path = document.createElementNS(svg.namespaceURI, 'path');
        path.setAttribute('fill', style.backgroundColor);
        path.setAttribute('stroke', style.borderTopColor);
        path.setAttribute('stroke-linejoin', 'round');
        surfaces.append(path);
        const border = parseFloat(style.borderTopWidth) || 0;
        // Preserve border-box geometry and clip any partially scrolled control.
        const points = this.roundedPoints(x+border/2, y+border/2, rect.width-border, rect.height-border, Math.max(0,radius-border/2));
        if (viewport) points.forEach(p => { p[1] = clamp(p[1], viewport.top-mr.top, viewport.bottom-mr.top); });
        this.surfaces.push({ path, points, border, centerY: y+rect.height/2 });
      });

      // Clone plain markup, never the predictive-search custom element.
      const template = this.form.cloneNode(true);
      template.classList.add('sb-clone'); template.removeAttribute('id'); template.removeAttribute('role');
      template.querySelectorAll('[id], [for], [aria-controls], [aria-activedescendant], [aria-labelledby], [aria-describedby]').forEach(node => {
        ['id', 'for', 'aria-controls', 'aria-activedescendant', 'aria-labelledby', 'aria-describedby'].forEach(attr => node.removeAttribute(attr));
      });
      const inputs = this.form.querySelectorAll('input');
      template.querySelectorAll('input').forEach((input, i) => { input.value = inputs[i].value; input.setAttribute('value', input.value); input.tabIndex = -1; });
      template.querySelectorAll(controls).forEach(control => {
        control.style.setProperty('background', 'transparent', 'important');
        control.style.setProperty('border-color', 'transparent', 'important');
        control.style.setProperty('box-shadow', 'none', 'important');
        control.style.setProperty('backdrop-filter', 'none', 'important');
        control.style.setProperty('-webkit-backdrop-filter', 'none', 'important');
        control.style.setProperty('transition', 'none', 'important');
      });
      template.querySelectorAll('.search-popdown__results > *').forEach(node => {
        if (node.matches(results ? '.predictive-search' : '.popular-searches')) node.classList.add('sb-live');
        else node.remove();
      });
      const scroll = this.form.querySelector(results ? '.predictive-search' : '.popular-searches')?.scrollTop || 0;
      const count = clamp(Math.ceil(height/4), 64, 112);
      const dpr = window.devicePixelRatio || 1;
      const fragment = document.createDocumentFragment();
      for (let i = 0; i < count; i++) {
        // Shared pixel-aligned source boundaries prevent each text copy from
        // being rasterized at a different fractional vertical offset.
        const start = Math.round(height*i/count*dpr)/dpr;
        const end = i === count-1 ? height : Math.round(height*(i+1)/count*dpr)/dpr;
        const length = Math.min(height-start, end-start+.25/dpr);
        const node = document.createElement('div'); node.className = 'sb-strip';
        Object.assign(node.style, { width: `${width}px`, height: `${length}px` });
        const copy = template.cloneNode(true);
        Object.assign(copy.style, { top: `${-start}px`, width: `${width}px`, height: `${height}px` });
        node.append(copy); fragment.append(node);
        this.strips.push({ node, start, length });
      }
      layer.append(fragment, svg); this.body.append(layer); this.layers.push(layer);
      if (scroll) layer.querySelectorAll('.sb-live').forEach(node => { node.scrollTop = scroll; });
      this.wasInert = this.main.inert;
      this.main.inert = true; this.main.style.opacity = '0';
      this.draw();
      return true;
    }
    screenEdge(y) {
      const e = edge(1-y/this.from.width, clamp(this.q), this.from, this.to);
      return { x: -e.x, top: e.top, bottom: e.bottom };
    }
    roundedPoints(x, y, width, height, radius) {
      const left = [], right = [], r = clamp(radius, 0, Math.min(width, height)/2);
      const add = (inset, dy) => {
        left.push([x+inset, y+dy]); right.push([x+width-inset, y+dy]);
      };
      // Uniform-angle corner samples keep the caps smooth near their tips,
      // where evenly spaced y samples otherwise produce long diagonal chords.
      if (r) {
        for (let i = 0; i <= 12; i++) {
          const angle = Math.PI*i/24;
          add(r-r*Math.sin(angle), r-r*Math.cos(angle));
        }
      } else add(0, 0);
      const straight = height-2*r, steps = Math.ceil(straight/2);
      for (let i = 1; i <= steps; i++) add(0, r+straight*i/steps);
      if (r) {
        for (let i = 1; i <= 12; i++) {
          const angle = Math.PI*i/24;
          add(r-r*Math.cos(angle), height-r+r*Math.sin(angle));
        }
      }
      return [...left, ...right.reverse()];
    }
    draw() {
      if (!this.clip) return;
      for (const s of this.strips) s.node.style.transform = matrix(s.length, this.from.height, this.screenEdge(s.start), this.screenEdge(s.start+s.length));
      for (const surface of this.surfaces) {
        const path = surface.points.map(([x,y], i) => {
          const e = this.screenEdge(y);
          return `${i ? 'L' : 'M'}${(mix(e.top,e.bottom,x/this.from.height)-this.bounds.left).toFixed(3)} ${(e.x-this.bounds.top).toFixed(3)}`;
        }).join(' ')+' Z';
        surface.path.setAttribute('d', path);
        const e = this.screenEdge(surface.centerY);
        surface.path.setAttribute('stroke-width', surface.border*Math.sqrt(Math.max(0,(e.bottom-e.top)/this.from.height)));
      }
      // One continuous backdrop, with rounded source corners mapped through
      // exactly the same funnel as the content. No per-strip blur seams.
      const left = [], right = [], h = this.from.width, r = this.radius;
      for (let i = 0; i <= 96; i++) {
        const y = h*i/96, e = this.screenEdge(y), d = Math.max(0, r-Math.min(y, h-y));
        const inset = (r-Math.sqrt(Math.max(0, r*r-d*d))) / this.from.height;
        left.push([mix(e.top, e.bottom, inset)-this.bounds.left, e.x-this.bounds.top]);
        right.push([mix(e.bottom, e.top, inset)-this.bounds.left, e.x-this.bounds.top]);
      }
      const points = [...left, ...right.reverse()];
      const path = points.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(3)} ${p[1].toFixed(3)}`).join(' ')+' Z';
      this.clip.style.clipPath = `path("${path}")`;
      this.outline.setAttribute('d', path);
    }
    run(target) {
      this.target = target;
      if (this.motion.matches) {
        // The theme adds is-open in its next animation frame.
        cancelAnimationFrame(this.frame); this.frame = requestAnimationFrame(() => this.finish()); return;
      }
      if (!this.prepare()) { this.frame = requestAnimationFrame(() => this.finish()); return; }
      if (this.frame) return; // Preserve velocity and mesh on reversal.
      this.last = performance.now();
      this.frame = requestAnimationFrame(now => this.tick(now));
    }
    tick(now) {
      const dt = Math.min((now-this.last)/1000, .04); this.last = now;
      // Exact critically damped spring solution; stable across frame rates.
      const omega = 9.25/(this.duration/1000), x = this.q-this.target;
      const c = this.velocity+omega*x, decay = Math.exp(-omega*dt);
      this.q = this.target+(x+c*dt)*decay;
      this.velocity = (this.velocity-omega*c*dt)*decay;
      this.draw();
      const absorbed = this.target === 1 && this.q >= .94;
      if (absorbed || (Math.abs(this.q-this.target) < .0011 && Math.abs(this.velocity) < .025)) this.finish();
      else this.frame = requestAnimationFrame(time => this.tick(time));
    }
    finish() {
      this.q = this.target; this.velocity = 0;
      this.clear();
      if (this.target === 0 && this.details.open) {
        this.main.style.opacity = '1'; this.restoreResults();
        this.original.transition.call(this.host, { target: this.row, propertyName: 'transform' });
      } else if (this.details.open) {
        this.original.transition.call(this.host, { target: this.row, propertyName: 'transform' });
        // Return keyboard focus only when it was inside the closing dialog.
        if (this.body.contains(document.activeElement) || document.activeElement === document.body) this.toggle.focus({ preventScroll: true });
      }
    }
    clear() {
      cancelAnimationFrame(this.frame); this.frame = 0;
      this.layers.forEach(node => node.remove()); this.layers = []; this.strips = []; this.surfaces = []; this.clip = null;
      this.main.style.opacity = ''; this.main.inert = this.wasInert || false;
      this.details.classList.remove('sb-closing');
    }
    resize() {
      if (!this.details.open) return;
      if (!this.host.offsetParent) { this.host.onClose(); return; }
      if (this.frame) this.finish();
      this.place();
    }
    destroy() {
      if (this.details.open) this.host.onClose();
      this.clear(); this.events.abort();
      this.host.open = this.original.open; this.host.close = this.original.close;
      this.host.onClose = this.original.onClose; this.host.popdownTransitionCallback = this.original.transition;
      this.host.classList.remove('sb-genie');
    }
  }
  const instances = new Map();
  async function init(root) {
    await customElements.whenDefined('search-popdown');
    root.querySelectorAll('[data-sb-enabled="true"] search-popdown[data-popdown-in-header]').forEach(host => {
      if (!instances.has(host) && host.isConnected) instances.set(host, new SearchGenie(host, host.closest('[data-sb-enabled]')));
    });
  }
  window.ByMorningGenie = { init };
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [host, instance] of instances) if (event.target.contains(host)) { instance.destroy(); instances.delete(host); }
  });
  init(document);
})();
